import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { from, Observable, of, throwError, timer } from 'rxjs';
import { switchMap, shareReplay, retryWhen, mergeMap, retry, catchError, tap } from 'rxjs/operators';

import { HttpConfig } from '../models/http-config.model';
import { SessionStorage } from './session-storage.service';
import { configureApiShared, getApiKey, getSharedDirectories, invalidateApiKey } from './api-shared';

@Injectable({
    providedIn: 'root'
})
export class HttpService {
    public baseURL: string;
    public mode: 'live'|'test';
    public params = { origin_app: '', origin_cta: '' };
    public extraParams = { origin_app: '', origin_cta: '' };
    public appKey: string;
    public apiKey: string;
    public lang = 'en';
    public view_id: string;
    public furthest_step: string;
    public view_params: any = {};
    public steps = ['modal','paymentform','transaction','success'];

    public xWidget = 'wc-vouchers';

    private publicKey: string;
    private publicKeyCache$: Observable<string> | null = null;
    public shared_storage = true;

    constructor(
        private http: HttpClient,
        private sessionStorage: SessionStorage
    ) { }

    public get isTestMode(): boolean {
        return this.mode === 'test';
    }

    public configureSharedApi(): void {
        configureApiShared({
            baseURL: this.baseURL,
            appKey: this.appKey,
            apiKey: this.apiKey,
            xWidget: this.xWidget
        });
    }

    public getPublicKey(): Observable<string> {
        if(this.shared_storage && this.appKey && this.apiKey){
            this.configureSharedApi();
            return from(getApiKey()).pipe(tap((token: string) => {
                this.publicKey = token;
                this.storeDirectories(getSharedDirectories());
            }));
        }
    // 1. Return in-memory cached token if present
    if (this.publicKey) {
        return of(this.publicKey);
    }

    // 2. Check session storage
    const storedKey = this.getKeyFromSessionStorage();
    if (storedKey) {
        this.publicKey = storedKey;
        return of(storedKey);
    }

    // 3. Return active in-flight request if multiple widgets trigger this simultaneously
    if (this.publicKeyCache$) {
        return this.publicKeyCache$;
    }

    // 4. Fetch new key from API and cache the Observable stream across all subscribers
    this.publicKeyCache$ = this.http.get<any>(`${this.baseURL}/auths?access=public`, {
        observe: 'response',
        responseType: 'text' as 'json',
        headers: {
            'X-API-KEY': this.apiKey,
            'X-APP-KEY': this.appKey,
            'X-WIDGET': this.xWidget
        }
    }).pipe(
        switchMap(response => this.cleanResponse(response)),
        switchMap(data => {
            const token = data.body?.auths?.jwt;
            this.publicKey = token; // Cache value for instant synchronous returns
            this.sessionStorage.setItem(this.getPublicKeyStorageKey(), token);
            this.storeDirectories(data.body?.directories);
            
            // Clean up the stream reference after successful emission
            this.publicKeyCache$ = null;
            return of(token);
        }),
        catchError(err => {
            // Clear the cached observable on error so retries can try again
            this.publicKeyCache$ = null;
            return throwError(() => err);
        }),
        shareReplay({
            bufferSize: 1,
            refCount: false // Keeps result alive for concurrent callers
        })
    );

    return this.publicKeyCache$;
}

public getMissingDirectories(requiredDirectories: string[]): string[] {
    this.sessionStorage.setItem('requiredDirectories', JSON.stringify(requiredDirectories));

    const directories = this.getStoredDirectories();

    return requiredDirectories.filter(directory => !this.hasDirectory(directories, directory));
}

public getStoredDirectories(): any {
    const directories = this.sessionStorage.getItem('directories');

    if (!directories) return {};

    try {
        return JSON.parse(directories) || {};
    } catch (err) {
        return {};
    }
}

public storeDirectories(directories: any): any {
    if (!directories) return this.getStoredDirectories();

    const storedDirectories = this.getStoredDirectories();
    const mergedDirectories = { ...storedDirectories, ...this.normaliseDirectories(directories) };

    this.sessionStorage.setItem('directories', JSON.stringify(mergedDirectories));

    return mergedDirectories;
}


public invalidatePublicKey(): void {
    if(this.shared_storage){
        invalidateApiKey();
    }
    this.sessionStorage.removeItem(this.getPublicKeyStorageKey());
    this.publicKey = undefined;
    this.publicKeyCache$ = null; // Instantly clears shared stream for subsequent retries
}

    public post(url: string, body: any, config?: HttpConfig): Observable<HttpResponse<any>> {
    return this.makeAuthenticatedRequest('POST', url, config, body);
}

public get(url: string, config?: HttpConfig): Observable<HttpResponse<any>> {
    return this.makeAuthenticatedRequest('GET', url, config);
}

public put(url: string, body: object, config?: HttpConfig): Observable<HttpResponse<any>> {
    return this.makeAuthenticatedRequest('PUT', url, config, body);
}

public patch(url: string, body: object, config?: HttpConfig): Observable<HttpResponse<any>> {
    return this.makeAuthenticatedRequest('PATCH', url, config, body);
}

public delete(url: string, config?: HttpConfig): Observable<HttpResponse<any>> {
    return this.makeAuthenticatedRequest('DELETE', url, config);
}


public getFile(url: string, config?: HttpConfig) {
    return this.http.get(`${this.baseURL}${url}`, {
        responseType: 'blob', observe: 'response', ...this.parseConfig(config)
    }).pipe(
        retry({count:2,delay:(err, i) => {
            if (i > 2 || (err.status !== 401 && err.status !== 403)) return throwError(()=>err);

            this.sessionStorage.removeItem('publicKey');
            this.publicKey = undefined;

            return timer(2000 * i);
        }})
    );
}

private makeAuthenticatedRequest(method: string, url: string, config?: HttpConfig, body?: any, retryCount: number = 0): Observable<HttpResponse<any>> {
    return of(null).pipe(
        switchMap(() => this.getRequestApiKey(config)),
        switchMap(publicKey => {
            // Create config with fresh auth token
            const authenticatedConfig = this.parseConfigWithAuth(config, publicKey);
            
            let httpCall: Observable<HttpResponse<any>>;
            
            switch (method) {
                case 'GET':
                    httpCall = this.http.get<any>(`${this.baseURL}${url}`, {
                        observe: 'response',
                        responseType: 'text' as 'json',
                        ...authenticatedConfig
                    });
                    break;
                case 'POST':
                    httpCall = this.http.post<any>(`${this.baseURL}${url}`, body, {
                        observe: 'response',
                        responseType: 'text' as 'json',
                        ...authenticatedConfig
                    });
                    break;
                case 'PUT':
                    httpCall = this.http.put<any>(`${this.baseURL}${url}`, body, {
                        observe: 'response',
                        responseType: 'text' as 'json',
                        ...authenticatedConfig
                    });
                    break;
                case 'PATCH':
                    httpCall = this.http.patch<any>(`${this.baseURL}${url}`, body, {
                        observe: 'response',
                        responseType: 'text' as 'json',
                        ...authenticatedConfig
                    });
                    break;
                case 'DELETE':
                    httpCall = this.http.delete<any>(`${this.baseURL}${url}`, {
                        observe: 'response',
                        responseType: 'text' as 'json',
                        ...authenticatedConfig
                    });
                    break;
            }
            
            return httpCall.pipe(
                switchMap(response => this.cleanResponse(response)),
                catchError(err => {
                    // If 401/403, clear the key and rethrow for retry
                    if (err.status === 401 || err.status === 403) {
                        this.invalidatePublicKey();
                    }
                    return throwError(() => err);
                })
            );
        }),
        retry({
            count: 2,
            delay: (err, retryIndex) => {
                if (retryIndex > 2 || (err.status !== 401 && err.status !== 403)) {
                    return throwError(() => err);
                }
                return timer(2000 * retryIndex);
            }
        }
        ));
    }

    private getRequestApiKey(config?: HttpConfig): Observable<string> {
        const explicitKey = config?.headers?.['X-API-KEY'];

        if (explicitKey) return of(explicitKey);

        return this.getPublicKey();
    }


    private parseConfigWithAuth(config?: HttpConfig, publicKey?: string): HttpConfig {
        const newConfig: HttpConfig = {
            headers: { 
                'X-APP-KEY': this.appKey,
                'X-WIDGET' : this.xWidget
            }
        };

        // Add authorization header if we have a public key
        if (publicKey) {
            newConfig.headers['X-API-KEY'] = publicKey;
        }
        if(config?.headers?.['X-API-KEY']){
            newConfig.headers['X-API-KEY'] = config.headers['X-API-KEY'];
        }

        newConfig.params = this.filterParams({ ...(config ? config.params : {}), ...this.params });

        if (config && config.headers) {
            newConfig.headers = { ...newConfig.headers, ...this.filterParams(config.headers) };
        }
        
        if (config && config.body) {
            newConfig.body = this.filterParams(config.body);
        }

        return newConfig;
    }

    public enocodeJSON(element: any, key?: string, list?: any[]): string {
        list = list || [];

        if (typeof (element) === 'object') {
            for (const idx in element) this.enocodeJSON(element[idx], key ? key + '[' + idx + ']' : idx, list);
        } else {
            list.push(key + '=' + encodeURIComponent(element));
        }

        return list.join('&');
    }

    private parseConfig(config?: HttpConfig): HttpConfig {
        const newConfig: HttpConfig = {
            headers: { 'X-APP-KEY': this.appKey
                    }
        };

        newConfig.params = this.filterParams({ ...(config ? config.params : {}), ...this.params });

        if (config && config.headers) newConfig.headers = { ...newConfig.headers, ...this.filterParams(config.headers)};
        newConfig.headers['X-WIDGET'] = this.xWidget;
        if (config && config.body) newConfig.body = this.filterParams(config.body);

        return newConfig;
    }

    private filterParams(params: { [key: string]: any }): any {
        const data: any = {};

        Object.keys(params).forEach(key => {
            if (!params.hasOwnProperty(key) || !params[key]) return;

            if (params[key] === true || params[key] === false) data[key] = params[key] ? '1' : '0';
            else data[key] = params[key];
        });

        return data;
    }

    private getKeyFromSessionStorage(): string {
        const key = this.sessionStorage.getItem(this.getPublicKeyStorageKey());

        try {
            JSON.parse(key);

            return null;
        } catch (err) {
            return key;
        }
    }
    
    private getPublicKeyStorageKey(): string {
        return ['publicKey', this.appKey || '', this.xWidget || ''].join(':');
    }

    private hasDirectory(directories: any, directory: string): boolean {
        return Object.prototype.hasOwnProperty.call(directories || {}, directory) && directories[directory] !== null && directories[directory] !== undefined;
    }

    private normaliseDirectories(directories: any): any {
        if (!directories || typeof directories !== 'object') return {};
        if (!Array.isArray(directories)) return directories;

        return directories.reduce((items, directory) => {
            if (!directory || typeof directory !== 'object') return items;

            const directoryName = directory.directory || directory.name || directory.key || directory.type;
            const directoryValue = directory.response ?? directory.data ?? directory.body ?? directory.items ?? directory.value;

            if (directoryName && directoryValue !== undefined) {
                items[directoryName] = directoryValue;
                return items;
            }

            return { ...items, ...directory };
        }, {});
    }


    private cleanResponse(response: HttpResponse<any>): Observable<HttpResponse<any>> {
        const contentType = response.headers.get('Content-Type') || '';
        const shouldParseJson = contentType.includes('application/json');

        if (!shouldParseJson) {
            // Return cleaned text without parsing
            return of(response);
        }

        try {
            // Parse the cleaned JSON
            const cleanBody = this.cleanPhpErrors(response.body);
            const parsedBody = JSON.parse(cleanBody);
            
            // Return new response with cleaned body
            return of(new HttpResponse({
                body: parsedBody,
                headers: response.headers,
                status: response.status,
                statusText: response.statusText,
                url: response.url
            }));
        } catch (error) {
            return throwError(() => new Error('Invalid Response'));
        }
    }

    private cleanPhpErrors(responseText: string): string {
        if (!responseText) return responseText;
        
        // Remove PHP errors/warnings/notices from the beginning
        // Look for the first occurrence of { or [ which indicates JSON start
        const jsonStartRegex = /^[\s\S]*?([{\[])/;
        const match = responseText.match(jsonStartRegex);
        
        if (match) {
            // Return everything starting from the first JSON character
            const startIndex = responseText.indexOf(match[1]);
            return responseText.substring(startIndex);
        }
        
        // If no JSON found, return original (might not be JSON response)
        return responseText;
    }

    public logWidgetView(step, params={}){
        let method = 'post';
        if(this.view_id){
            method = 'put';
        }

        this.view_params = { ...(this.view_params || {}), ...(params || {}) };

        const adId = this.view_params['adId'] || this.view_params['ad_id'] || this.view_params['affiliate_id'] || this.view_params['affiliateId'];
        const affiliateId = this.view_params['affiliate_id'] || this.view_params['affiliateId'] || this.view_params['adId'] || this.view_params['ad_id'];
        const splitTest = this.view_params['splitTest'] || this.view_params['split_test'];

        if(adId){
            this.view_params['adId'] = adId;
        }

        if(affiliateId){
            this.view_params['affiliate_id'] = affiliateId;
            this.view_params['affiliateId'] = affiliateId;
        }

        if(splitTest){
            this.view_params['splitTest'] = splitTest;
            this.view_params['split_test'] = splitTest;
        }

        if(this.view_params.total == 0) return;
        //steps
        if(this.steps.indexOf(step) > this.steps.indexOf(this.furthest_step)){
            this.furthest_step = step;
        }
        if(!this.view_params?.steps){
            this.view_params.steps = [];
        }
        if(this.view_params.steps.indexOf(step) === -1){
            this.view_params.steps.push(step);
        }else{
            return; //only once per step
        }
        //console.log('step',step, this.view_params);

        this[method]('/widget_views',{
            widget_view_id: this.view_id,
            affiliate_id: this.view_params.affiliate_id,
            split_test: this.view_params.split_test,
            company_id: this.view_params.company_id,
            widget: this.xWidget,
            ts: ""+Math.floor(Date.now()/1000),
            domain: window.location.hostname,
            referrer: window.location.href,
            step,
            user_agent: navigator.userAgent,
            furthest_step: this.furthest_step,
            params: JSON.stringify(this.view_params)
        }).subscribe((response: any) => {
            if(method == 'post' && response.body?.widget_views?.[0]?.widget_view_id){
                this.view_id = response.body.widget_views[0].widget_view_id;
            }
        });

    }

    
}
